/**
 * PATH: packages/ui/src/kanban/components/KanbanMergeCardsDialog.tsx
 * FILE_ID: UI-KANBAN-MERGE-CARDS-001
 * PURPOSE: Modal that lets an operator manually fold one or more
 *          cards into the focused (keeper) card.
 * WHY:
 *   Operator complaint 2026-05-08: "I should be able to ...
 *   create a multi email collapse on the same card as a task if
 *   that makes sense."
 *
 *   The auto-thread-merge path covers cards that share an
 *   outlook_conversation_id. Operators need a manual path for the
 *   case where two cards belong to the same situation but came in
 *   on different threads — supplier replied from a different
 *   mailbox, customer forwarded from a personal address, two
 *   variations the operator wants tracked as one, etc.
 *
 *   This dialog renders a searchable list of OPEN cards on the
 *   same board (excluding the keeper), lets the operator tick the
 *   ones to absorb, shows a preview of "this many emails will
 *   move", and posts to /cards/{keeper}/merge/. The backend
 *   reuses the same atomic primitive as the auto-merge path so
 *   the audit log shape is consistent.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Merge, Search, X } from 'lucide-react'

import type { KanbanApi } from '../api'
import type { Card } from '../types'
import { useFocusTrap } from '../hooks/useFocusTrap'

export interface KanbanMergeCardsDialogProps {
  open: boolean
  /** The card every selected loser will be merged INTO. */
  keeper: Card
  /** Pool of OPEN cards on the same board. Caller is expected to
   *  pre-filter by board / archive status — this dialog just
   *  renders the result. */
  candidates: Card[]
  api: KanbanApi
  /** Optional list of loser card ids to start with already
   *  ticked. Used by the soft-match chip in the drawer to
   *  open the dialog one click closer to "Merge". */
  preselectLoserIds?: string[]
  onClose: () => void
  /** Fired after a successful merge. `mergedIds` is the list of losers
   *  that were folded in. `keeper` is the FRESH keeper card returned by
   *  the merge endpoint — it already carries the new merge audit row
   *  (with `merged_card_id`) + the grown `related_emails`, so the caller
   *  should prefer it over its stale pre-merge copy. Optional 2nd arg to
   *  preserve the pre-2026-07-06 callback contract. */
  onMerged?: (mergedIds: string[], keeper?: Card) => void
}

export function KanbanMergeCardsDialog({
  open,
  keeper,
  candidates,
  api,
  preselectLoserIds,
  onClose,
  onMerged,
}: KanbanMergeCardsDialogProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  // Escape-to-close lives in the busy-aware effect below; trap Tab only.
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, { active: open })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset on open so a previous run doesn't leak into the next.
  // The preselect honour ensures the soft-match chip's "click
  // through to merge" lands on a dialog already showing the
  // suggested loser as ticked.
  useEffect(() => {
    if (open) {
      setSelected(new Set(preselectLoserIds ?? []))
      setQuery('')
      setReason('')
      setError(null)
      setBusy(false)
    }
  }, [open, keeper.id, preselectLoserIds])

  // Esc → close (only when not busy — slow merges shouldn't be
  // cancellable, the audit log row is already in flight).
  useEffect(() => {
    if (!open) return
    function esc(e: KeyboardEvent) {
      if (busy) return
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [open, busy, onClose])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return candidates
      .filter((c) => c.id !== keeper.id)
      .filter((c) =>
        q
          ? (c.title || '').toLowerCase().includes(q)
            || (c.summary || '').toLowerCase().includes(q)
          : true,
      )
      .slice(0, 200) // hard cap — past 200 it's a search problem, not a merge problem
  }, [candidates, keeper.id, query])

  const totalEmailsToMove = useMemo(() => {
    let n = 0
    for (const c of candidates) {
      if (!selected.has(c.id)) continue
      // primary + related — no dedupe across losers (rough preview).
      n += (c.primary_email ? 1 : 0) + (c.related_email_count ?? 0)
    }
    return n
  }, [candidates, selected])

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const submit = async () => {
    if (!api.mergeCards) {
      setError('Merge endpoint not wired on this host.')
      return
    }
    if (selected.size === 0) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.mergeCards(keeper.id, Array.from(selected), reason || undefined)
      // Prefer the fresh keeper (carries the new merge audit row) so the
      // drawer can render the "Open merged-in card" affordance immediately
      // (Fix #4) instead of after a background refetch.
      onMerged?.(res.merged ?? [], res.card)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Merge cards"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (busy) return
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={wrapRef}
        className="flex h-[min(560px,85vh)] w-[min(640px,92vw)] flex-col rounded-lg border border-neutral-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-2xl"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-neutral-100 dark:border-zinc-800 px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900 dark:text-zinc-100">
              <Merge className="h-4 w-4" /> Merge into "{keeper.title}"
            </h2>
            <p className="mt-0.5 text-[11.5px] leading-snug text-neutral-500 dark:text-zinc-400">
              Selected cards will be archived; their primary + related emails
              are moved onto the keeper. The action is recorded in every card's
              audit log.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-zinc-900 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Search */}
        <div className="border-b border-neutral-100 dark:border-zinc-800 px-3 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-neutral-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title or description…"
              className="w-full rounded-md border border-neutral-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 pl-7 text-sm text-neutral-900 dark:text-zinc-100 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
            />
          </div>
        </div>

        {/* Candidate list */}
        <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
          {filtered.length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-neutral-500">
              {query
                ? 'No cards match that search on this board.'
                : 'No other cards on this board to merge.'}
            </p>
          ) : (
            filtered.map((c) => {
              const checked = selected.has(c.id)
              return (
                <label
                  key={c.id}
                  className={[
                    'flex cursor-pointer items-start gap-2 rounded px-2 py-2 transition-colors',
                    checked
                      ? 'bg-blue-50 dark:bg-blue-950/30'
                      : 'hover:bg-neutral-50 dark:hover:bg-zinc-900/60',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(c.id)}
                    disabled={busy}
                    className="mt-0.5 h-3.5 w-3.5 accent-blue-600"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px] font-medium text-neutral-800 dark:text-zinc-100">
                      {c.title || '(untitled)'}
                    </p>
                    {c.summary && (
                      <p className="mt-0.5 line-clamp-2 text-[11px] text-neutral-500 dark:text-zinc-400">
                        {c.summary}
                      </p>
                    )}
                    <p className="mt-0.5 flex items-center gap-2 text-[10px] text-neutral-400">
                      {c.primary_email && <span>· 1 primary email</span>}
                      {c.related_email_count
                        ? <span>· {c.related_email_count} related email{c.related_email_count === 1 ? '' : 's'}</span>
                        : null}
                    </p>
                  </div>
                </label>
              )
            })
          )}
        </div>

        {/* Reason + Footer */}
        <div className="border-t border-neutral-100 dark:border-zinc-800 px-3 py-2">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, lands in the audit log)"
            disabled={busy}
            className="mb-2 w-full rounded-md border border-neutral-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1 text-[12px] text-neutral-900 dark:text-zinc-100 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:opacity-60"
          />
          {error && (
            <p className="mb-2 text-[11px] text-red-600 dark:text-red-400" role="alert">{error}</p>
          )}
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-neutral-500 dark:text-zinc-400">
              {selected.size === 0
                ? 'Pick at least one card to merge.'
                : `${selected.size} card${selected.size === 1 ? '' : 's'} → up to ${totalEmailsToMove} email${totalEmailsToMove === 1 ? '' : 's'} moved`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-md border border-neutral-300 dark:border-zinc-700 px-3 py-1.5 text-[12px] text-neutral-700 dark:text-zinc-200 hover:bg-neutral-100 dark:hover:bg-zinc-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={busy || selected.size === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                Merge {selected.size || ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
